import React, { useState, useEffect } from 'react';
import { academyAdminAPI } from '../../lib/academyApi';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, BookOpen, X, ListOrdered, Eye, PenLine } from 'lucide-react';
import OutlineEditor from '../../components/academy/OutlineEditor';
import TrainingPreview from '../../components/academy/TrainingPreview';
import ImagePicker from '../../components/cms/ImagePicker';

const modeOptions = [
  { value: 'presentiel', label: 'Presentiel' },
  { value: 'distance', label: 'A distance' },
  { value: 'hybride', label: 'Hybride' },
];
const statusOptions = [
  { value: 'publie', label: 'Publie' },
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'archive', label: 'Archive' },
];

const AcademyTrainings = () => {
  const [trainings, setTrainings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formTab, setFormTab] = useState('edit');
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return { title: '', slug: '', short_description: '', full_description: '', objectives: '', target_audience: '', prerequisites: '', duration: '', language: 'Francais', mode: 'presentiel', price: 0, promo_price: '', image_url: '', category_id: '', instructor_ids: [], outline: [], is_featured: false, status: 'publie', seo_title: '', seo_description: '' };
  }

  const fetchAll = async () => {
    try {
      const [t, c, i] = await Promise.all([academyAdminAPI.listTrainings(), academyAdminAPI.listCategories(), academyAdminAPI.listInstructors()]);
      setTrainings(t.data); setCategories(c.data); setInstructors(i.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ ...t, objectives: (t.objectives || []).join('\n'), promo_price: t.promo_price ?? '', instructor_ids: t.instructor_ids || [], outline: t.outline || [], seo_title: t.seo_title || '', seo_description: t.seo_description || '', image_url: t.image_url || '', target_audience: t.target_audience || '', prerequisites: t.prerequisites || '', short_description: t.short_description || '', full_description: t.full_description || '', category_id: t.category_id || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) { toast.error('Titre et slug requis'); return; }
    const payload = { ...form, objectives: form.objectives.split('\n').filter(Boolean), price: parseFloat(form.price) || 0, promo_price: form.promo_price ? parseFloat(form.promo_price) : null, outline: form.outline || [] };
    try {
      if (editing) { await academyAdminAPI.updateTraining(editing, payload); toast.success('Formation mise a jour'); }
      else { await academyAdminAPI.createTraining(payload); toast.success('Formation creee'); }
      setShowForm(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette formation?')) return;
    try { await academyAdminAPI.deleteTraining(id); toast.success('Supprimee'); fetchAll(); }
    catch (e) { toast.error('Erreur'); }
  };

  const toggleInstructor = (id) => {
    setForm(f => ({ ...f, instructor_ids: f.instructor_ids.includes(id) ? f.instructor_ids.filter(i => i !== id) : [...f.instructor_ids, id] }));
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" /></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="academy-trainings-admin">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl font-bold text-[#1D1D1B]">Formations</h1><p className="text-neutral-500 text-sm">{trainings.length} formation(s)</p></div>
        <Button onClick={openNew} data-testid="add-training-btn"><Plus className="w-4 h-4 mr-2" />Ajouter</Button>
      </div>

      {/* List */}
      <div className="space-y-3">
        {trainings.map(t => (
          <Card key={t.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-neutral-100">
                {t.image_url ? <img src={t.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-6 h-6 text-neutral-300" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-[#1D1D1B] truncate">{t.title}</h3>
                  <Badge variant={t.status === 'publie' ? 'default' : 'secondary'} className="text-xs">{t.status}</Badge>
                  {t.is_featured && <Badge className="text-xs bg-[#F9B438]/20 text-[#F9B438]">Vedette</Badge>}
                </div>
                <p className="text-sm text-neutral-500 truncate">{t.short_description}</p>
                <div className="flex gap-3 mt-1 text-xs text-neutral-400">
                  <span>{t.duration}</span><span>{t.mode}</span><span>{t.price > 0 ? `${t.price} $` : 'Gratuit'}</span>
                  {t.outline?.length > 0 && <span className="flex items-center gap-1"><ListOrdered className="w-3 h-3" />{t.outline.length} section(s)</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-training-${t.slug}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Form Modal - Split Panel */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-6 overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <div className="relative w-full max-w-7xl z-10 my-4 flex flex-col lg:flex-row gap-0 bg-white rounded-xl shadow-2xl overflow-hidden border" style={{ maxHeight: 'calc(100vh - 48px)' }}>

            {/* Left Panel: Editor */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center justify-between px-6 py-4 border-b bg-white sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-lg">{editing ? 'Modifier' : 'Ajouter'} une formation</CardTitle>
                  {/* Mobile tab toggle */}
                  <div className="flex lg:hidden border rounded-lg overflow-hidden ml-2">
                    <button onClick={() => setFormTab('edit')} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${formTab === 'edit' ? 'bg-[#1D1D1B] text-white' : 'text-neutral-500 hover:bg-neutral-50'}`} data-testid="tab-edit">
                      <PenLine className="w-3 h-3" />Editeur
                    </button>
                    <button onClick={() => setFormTab('preview')} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${formTab === 'preview' ? 'bg-[#1D1D1B] text-white' : 'text-neutral-500 hover:bg-neutral-50'}`} data-testid="tab-preview">
                      <Eye className="w-3 h-3" />Apercu
                    </button>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
              </div>

              {/* Editor Content - visible on desktop always, on mobile only when tab=edit */}
              <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${formTab !== 'edit' ? 'hidden lg:block' : ''}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Titre *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]/g,'-').replace(/-+/g,'-')})} /></div>
                  <div><Label>Slug *</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} /></div>
                </div>
                <div><Label>Description courte</Label><Textarea value={form.short_description} onChange={e => setForm({...form, short_description: e.target.value})} rows={2} /></div>
                <div><Label>Description complete</Label><Textarea value={form.full_description} onChange={e => setForm({...form, full_description: e.target.value})} rows={4} /></div>
                <div><Label>Objectifs (un par ligne)</Label><Textarea value={form.objectives} onChange={e => setForm({...form, objectives: e.target.value})} rows={3} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Public cible</Label><Input value={form.target_audience} onChange={e => setForm({...form, target_audience: e.target.value})} /></div>
                  <div><Label>Prerequis</Label><Input value={form.prerequisites} onChange={e => setForm({...form, prerequisites: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Duree</Label><Input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} placeholder="Ex: 2 jours" /></div>
                  <div><Label>Langue</Label><Input value={form.language} onChange={e => setForm({...form, language: e.target.value})} /></div>
                  <div><Label>Mode</Label><select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={form.mode} onChange={e => setForm({...form, mode: e.target.value})}>{modeOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Prix ($)</Label><Input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} /></div>
                  <div><Label>Prix promo ($)</Label><Input type="number" value={form.promo_price} onChange={e => setForm({...form, promo_price: e.target.value})} placeholder="Optionnel" /></div>
                  <div><Label>Statut</Label><select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>{statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
                </div>
                <ImagePicker value={form.image_url} onChange={url => setForm({...form, image_url: url})} folder="formations" label="Image de la formation" />
                <div><Label>Categorie</Label><select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})}><option value="">-- Aucune --</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div>
                  <Label>Instructeurs</Label>
                  <div className="flex flex-wrap gap-2 mt-1">{instructors.map(i => <Badge key={i.id} className={`cursor-pointer ${form.instructor_ids.includes(i.id) ? 'bg-[#F9B438] text-[#1D1D1B]' : 'bg-neutral-100 text-neutral-500'}`} onClick={() => toggleInstructor(i.id)}>{i.name}</Badge>)}</div>
                </div>
                {/* Outline Editor */}
                <div className="border-t pt-4">
                  <OutlineEditor outline={form.outline} onChange={(outline) => setForm({...form, outline})} />
                </div>
                <div className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})} /><Label>Formation vedette</Label></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>SEO Titre</Label><Input value={form.seo_title} onChange={e => setForm({...form, seo_title: e.target.value})} /></div>
                  <div><Label>SEO Description</Label><Input value={form.seo_description} onChange={e => setForm({...form, seo_description: e.target.value})} /></div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Annuler</Button>
                  <Button onClick={handleSave} className="flex-1" data-testid="save-training-btn">{editing ? 'Enregistrer' : 'Creer'}</Button>
                </div>
              </div>

              {/* Mobile preview - visible only when tab=preview on mobile */}
              <div className={`flex-1 overflow-y-auto lg:hidden ${formTab !== 'preview' ? 'hidden' : ''}`}>
                <div className="bg-neutral-50 min-h-full">
                  <TrainingPreview form={form} categories={categories} instructors={instructors} />
                </div>
              </div>
            </div>

            {/* Right Panel: Preview (desktop only) */}
            <div className="hidden lg:flex flex-col w-[380px] border-l bg-neutral-50 flex-shrink-0">
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-neutral-50 sticky top-0 z-10">
                <Eye className="w-4 h-4 text-[#F9B438]" />
                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Apercu en direct</span>
              </div>
              <div className="flex-1 overflow-y-auto" data-testid="preview-panel">
                <TrainingPreview form={form} categories={categories} instructors={instructors} />
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyTrainings;
